import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from '@mui/material';
import { EinstellungenService } from '../services/einstellungenService';
import { DatevSettings } from '../types/einstellungen';

/**
 * Vorpruefung fuer das Eingabefeld und den Speichern-Button:
 * Leer ist erlaubt, weil dadurch neue Versandauftraege deaktiviert werden.
 * Ansonsten wird genau eine Adresse ohne Anzeigenamen oder Verteiler akzeptiert.
 * Die verbindliche Validierung erfolgt weiterhin im Backend.
 */
const validAddress = (value: string): boolean => !value || (
  value.length <= 254 && /^[^\s@,;<>"\r\n]+@[^\s@,;<>"\r\n]+\.[^\s@,;<>"\r\n]+$/.test(value)
);

/**
 * Administrative Einstellungsseite unter /einstellungen.
 * App.tsx schuetzt die Route mit AdminRoute; Navigation.tsx stellt den Tab bereit.
 *
 * Aufbau: Ueberschrift -> DATEV-Karte -> Meldungen -> Adressfeld -> Aktionen
 * -> fachlicher Hinweis zur Bezahlt-Markierung.
 * SMTP-Zugangsdaten werden hier weder bearbeitet noch vom Backend angefordert.
 */
const EinstellungenPage: React.FC = () => {
  // settings enthaelt den zuletzt geladenen Serverstand einschliesslich Versionsnummer.
  // email ist davon getrennt, damit Eingaben erst mit "Speichern" wirksam werden.
  const [settings, setSettings] = useState<DatevSettings | null>(null);
  const [email, setEmail] = useState('');
  // busy sperrt konkurrierende Bedienung; message steuert das Ergebnis-Alert.
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  /**
   * Liest GET /api/einstellungen/datev ueber den zentralen API-Service.
   * Wird beim Seitenaufruf und durch "Neu laden" ausgeloest; ungespeicherte
   * Eingaben werden dabei durch den aktuellen Serverstand ersetzt.
   */
  const load = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const data = await EinstellungenService.getDatev();
      setSettings(data);
      setEmail(data.datevEmail || '');
    } catch {
      setMessage({ severity: 'error', text: 'Einstellungen konnten nicht geladen werden.' });
    } finally {
      setBusy(false);
    }
  };

  // Einmaliges initiales Laden. settings bleibt bis zum Erfolg null:
  // Das Adressfeld und "Speichern" sind deshalb vorher deaktiviert.
  useEffect(() => { load(); }, []);

  /**
   * Speichert per PUT /api/einstellungen/datev die Adresse und bekannte Version.
   * null bedeutet "DATEV-Versand deaktivieren"; die Version schuetzt vor dem
   * Ueberschreiben zwischenzeitlicher Aenderungen durch andere Administratoren.
   * Nach Erfolg wird die neue Serverversion fuer den naechsten Speichervorgang
   * uebernommen. Backend-Fehlermeldungen erscheinen im selben Alert wie Ladefehler.
   */
  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setMessage(null);
    try {
      const data = await EinstellungenService.saveDatev(email.trim() || null, settings.version);
      setSettings(data);
      setEmail(data.datevEmail || '');
      setMessage({ severity: 'success', text: 'DATEV-Einstellungen gespeichert.' });
    } catch (error: any) {
      setMessage({ severity: 'error', text: error.response?.data?.message || 'Speichern fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Die begrenzte Breite haelt das einzelne Einstellungsformular lesbar.
    <Box maxWidth={700}>
      {/* Seitentitel und DATEV-Karte gruppieren die globale, nicht nutzerbezogene Einstellung. */}
      <Typography variant="h4" gutterBottom>Einstellungen</Typography>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>DATEV</Typography>
          {/* Erklaert den Geltungsbereich und die Aufteilung in eine E-Mail je Bescheid. */}
          <Typography variant="body2" color="text.secondary" mb={2}>
            Diese Adresse gilt für die gesamte Anwendung. Pro Gebührenbescheid wird eine E-Mail mit allen zugehörigen PDFs gesendet.
          </Typography>
          {/* Gemeinsamer Rueckmeldebereich fuer load()/save(): Erfolg gruen, Fehler rot. */}
          {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
          {/*
            SMTP-Warnung nur bei bereits gespeicherter Empfaengeradresse anzeigen.
            versandBereit stammt aus dem Backend: Das Frontend prueft weder das
            SMTP-Passwort noch die Verbindung. Die Warnung sperrt das Speichern
            der Empfaengeradresse nicht; sie betrifft die Versandbereitschaft.
          */}
          {settings && !settings.versandBereit && settings.datevEmail && (
            <Alert severity="warning" sx={{ mb: 2 }}>Der SMTP-Versand ist nicht bereit. Bitte die Server-Konfiguration prüfen.</Alert>
          )}
          {/*
            Kontrolliertes Eingabefeld: value liest den lokalen Entwurf, onChange
            aktualisiert ihn. error visualisiert die Vorpruefung, helperText
            erklaert das Leeren. maxLength begrenzt Eingaben auf die API-Laenge.
          */}
          <TextField
            fullWidth label="DATEV-E-Mail-Adresse" type="email" value={email}
            onChange={event => setEmail(event.target.value)} disabled={busy || !settings}
            error={!validAddress(email.trim())}
            helperText="Genau eine Adresse. Ein leeres Feld deaktiviert neue Versandaufträge. Laufende Aufträge behalten ihren Empfänger."
            inputProps={{ maxLength: 254 }}
          />
          {/* Aktionszeile: Speichern nur mit Serverstand und gueltigem Entwurf erlauben. */}
          <Box display="flex" gap={1} mt={2}>
            <Button variant="contained" onClick={save} disabled={busy || !settings || !validAddress(email.trim())}>
              Speichern
            </Button>
            {/* Neu laden funktioniert auch nach einem initialen Ladefehler. */}
            <Button onClick={load} disabled={busy}>Neu laden</Button>
            {/* Fortschrittsanzeige fuer beide HTTP-Aktionen, nicht fuer einen E-Mail-Versand. */}
            {busy && <CircularProgress size={24} />}
          </Box>
          {/* Fachliche Abgrenzung: SMTP-Annahme ist weder DATEV-Import noch Bankzahlung. */}
          <Typography variant="body2" color="text.secondary" mt={2}>
            Die erfolgreiche Übergabe an den SMTP-Server führt zur Bezahlt-Markierung. Sie bestätigt keine Verarbeitung durch DATEV und löst keine Zahlung aus.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EinstellungenPage;
